'use client';

import { motion } from 'framer-motion';
import { EnergySourceIcon } from '../atomic/EnergySourceIcon';
import { ProgressBar } from '../atomic/ProgressBar';
import { InfoTooltip } from '../atomic/Tooltip';

/**
 * [DC01] Molecular component that displays details about an energy source
 */
interface EnergySourceCardProps {
  name: string;
  installations: number;
  kwhPerYear: number;
  potentialEuro: number;
  maxKwh: number; // For calculating percentage
  color: string;
  className?: string;
  compact?: boolean;
}

// Named export for better tree-shaking and explicit imports
export function EnergySourceCard({
  name,
  installations,
  kwhPerYear,
  potentialEuro,
  maxKwh,
  color = 'bg-green-500',
  className = '',
  compact = false
}: EnergySourceCardProps) {
  // [CP01] Simple calculation with early return
  // [CP03] Early return for zero values - ultra compact display
  if (installations === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`${compact ? 'p-2' : 'p-3'} bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <EnergySourceIcon type={name} size={compact ? 14 : 16} className={`${compact ? 'mr-1' : 'mr-1.5'} opacity-50`} />
            <h3 className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-500 dark:text-gray-500`}>
              {name}
            </h3>
          </div>
          <div className="text-gray-400 dark:text-gray-600 text-xs">
            0 Anlagen
          </div>
        </div>
        <ProgressBar value={0} color={color} className="h-0.5 mt-1 opacity-30" />
        <div className="text-right text-xs text-gray-400 dark:text-gray-600 mt-0.5">
          €0/Jahr
        </div>
      </motion.div>
    );
  }
  
  // Calculate the percentage for the progress bar
  const percentage = Math.min(100, (kwhPerYear / maxKwh) * 100);
  
  // [NS01] Use descriptive names
  // [DM02] Focus on readability
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={{ 
        scale: 1.02,
        y: -2,
        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
        transition: { type: "spring", stiffness: 300, damping: 25 }
      }}
      whileTap={{ scale: 0.98 }}
      className={`${compact ? 'p-3' : 'p-4'} bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center">
          <EnergySourceIcon type={name} size={compact ? 16 : 20} className={compact ? 'mr-1.5' : 'mr-2'} />
          <h3 className={`${compact ? 'text-sm' : 'text-base'} font-medium text-gray-900 dark:text-white`}>
            {name}
          </h3>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {installations.toLocaleString('de-AT')} {installations === 1 ? 'Anlage' : 'Anlagen'}
        </div>
      </div>
      
      <div className="mb-1">
        <div className="flex justify-between text-xs mb-0.5">
          <span className="text-gray-600 dark:text-gray-300">
            {Math.round(kwhPerYear).toLocaleString('de-AT')} kWh
            <InfoTooltip 
              content={`Diese ${installations} ${name}-Anlagen produzieren zusammen ${Math.round(kwhPerYear).toLocaleString('de-AT')} kWh pro Jahr.`}
            />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            €{Math.round(potentialEuro).toLocaleString('de-AT')}/Jahr
            <InfoTooltip 
              content="Berechnet aus 40% handelbarer Energie × 0,106 €/kWh"
            />
          </span>
        </div>
        <ProgressBar value={percentage} color={color} className={compact ? 'h-1.5' : ''} />
      </div>
      
      {!compact && (
        <div className="text-right text-xs text-gray-500 dark:text-gray-400 mt-1">
          Potenzial zu {Math.round(percentage)}% ausgeschöpft
        </div>
      )}
    </motion.div>
  );
}
